import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { cardUnits } from '../../../functions';
import { ICard } from '../../../Models/CardModels';
import { IUnrevealedCard } from '../../../Models/StateModels';
import Card from '../Card/Card';
import { EmptySlot, FlameIcon } from '../FeltMat/feltParts';

export type PileHighlight = 'none' | 'turn' | 'drop' | 'danger';

interface IPileProps {
  playedCards: (ICard | IUnrevealedCard)[];
  highlight: PileHighlight;
  burnChance?: string;
  onPickup?: () => void;
}

const SHOWN_CARDS = 4;
const STACK_SHIFT = 2;
const STACK_TILT_DEGREES = 13;
const TOP_CARD_LEFT = 31;

const CARD_CLASS_NAME =
  'block max-w-none w-[calc(70*var(--card-unit))] h-[calc(102*var(--card-unit))] rounded-[6px] outline-1 -outline-offset-1 outline-black/25 shadow-pile-card';

const EDGE_LABEL_CLASS_NAME =
  'absolute bottom-0 left-1/2 flex -translate-x-1/2 items-center gap-[6px] rounded-full px-[10px] py-[5px] text-[12px] font-bold whitespace-nowrap shadow-felt-chip';

const HIGHLIGHT_CLASS_NAMES: Record<PileHighlight, string> = {
  none: 'shadow-none',
  turn: 'shadow-pile-turn',
  drop: 'shadow-pile-drop',
  danger: 'shadow-pile-danger',
};

export default function Pile({
  playedCards,
  highlight,
  burnChance,
  onPickup,
}: IPileProps) {
  const shownCards = playedCards.slice(-SHOWN_CARDS);
  const className = classNames(
    'relative block rounded-[16px] p-[8px] transition-shadow duration-150',
    HIGHLIGHT_CLASS_NAMES[highlight]
  );

  const cards = (
    <span className="relative block h-[calc(124*var(--card-unit))] w-[calc(120*var(--card-unit))]">
      {shownCards.length === 0 && (
        <EmptySlot className="absolute inset-0">Empty</EmptySlot>
      )}
      <AnimatePresence initial={false}>
        {shownCards.map((card, i) => {
          const depth = shownCards.length - 1 - i;
          return (
            <motion.span
              key={card.cardId}
              className="absolute top-[calc(11*var(--card-unit))] block transition-[left] duration-150"
              style={{ left: cardUnits(TOP_CARD_LEFT - depth * STACK_SHIFT) }}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{
                opacity: 1,
                scale: 1,
                rotate: -depth * STACK_TILT_DEGREES,
              }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <Card card={card} className={CARD_CLASS_NAME} />
            </motion.span>
          );
        })}
      </AnimatePresence>
    </span>
  );

  if (!onPickup) {
    return (
      <div className={className}>
        {cards}
        <AnimatePresence initial={false}>
          {burnChance && (
            <motion.span
              key="burn-chance"
              className={classNames(
                EDGE_LABEL_CLASS_NAME,
                'bg-burn text-burn-ink'
              )}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <FlameIcon size={14} />
              {burnChance}
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <button
      aria-label={`Pick up the pile, ${playedCards.length} cards`}
      onClick={onPickup}
      className={classNames(
        className,
        'cursor-pointer bg-[rgba(120,30,10,0.18)] outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-felt-ink'
      )}
    >
      {cards}
      <span
        className={classNames(
          EDGE_LABEL_CLASS_NAME,
          'bg-danger text-white normal-case tracking-normal'
        )}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 5v14M6 13l6 6 6-6" />
        </svg>
        Tap to pick up
      </span>
    </button>
  );
}
